import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { cn, validateQuoteForm } from "@/lib/utils";
import { SEOHead } from "@/components/shared/SEOHead";

export const Route = createFileRoute("/business/quote")({
  component: QuotePage,
});

interface QuoteProduct {
  id: string;
  product_type: string;
  quantity: string;
  sizes: string;
  colors: string;
  notes: string;
}

function QuotePage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [products, setProducts] = useState<QuoteProduct[]>([
    { id: crypto.randomUUID(), product_type: "", quantity: "", sizes: "", colors: "", notes: "" },
  ]);

  const addProduct = () => {
    setProducts([
      ...products,
      { id: crypto.randomUUID(), product_type: "", quantity: "", sizes: "", colors: "", notes: "" },
    ]);
  };

  const updateProduct = useCallback((id: string, field: keyof QuoteProduct, value: string) => {
    setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    // Clear field-level error
    setFieldErrors((prev) => {
      const next = { ...prev };
      // Find and clear product-specific errors
      const idx = products.findIndex((p) => p.id === id);
      if (idx >= 0) {
        delete next[`product_${idx}_type`];
        delete next[`product_${idx}_quantity`];
      }
      return next;
    });
  }, [products]);

  const removeProduct = (id: string) => {
    if (products.length > 1) {
      setProducts(products.filter((p) => p.id !== id));
    }
  };

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    const validation = validateQuoteForm({
      companyName,
      contactName,
      email,
      products,
    });

    if (!validation.valid) {
      setFieldErrors(validation.errors);
      // Focus first error
      const firstKey = Object.keys(validation.errors)[0];
      if (firstKey) {
        const el = document.getElementById(`quote-${firstKey}`);
        el?.focus();
      }
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      // In production, this would POST to /api/business/quote
      // For now, simulate a successful submission
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setSubmitted(true);
    } catch {
      setSubmitError("That didn't go through. Try again or email us at business@quuil.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="page-enter bp-utility-page bp-business-page">
        <SEOHead title="Quote Request Sent" description="Your bulk order quote request has been received." path="/business/quote" robots="noindex,follow" />
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-20 text-center">
          <div className="w-16 h-16 bg-qds-success-tint border border-qds-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-qds-success" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h1 className="text-2xl font-medium text-qds-text-primary mb-3">
            We're on It
          </h1>
          <p className="text-qds-text-secondary text-sm mb-8 max-w-md mx-auto leading-relaxed">
            Thanks, {contactName}. Your quote request is in and we'll have
            pricing to {email} within 24 hours.
          </p>
          <Link
            to="/business"
            className="inline-flex items-center gap-2 bg-qds-primary text-qds-text-inverse px-6 py-3 rounded-xl font-medium text-sm hover:bg-qds-primary-pressed transition-colors min-h-[44px]"
          >
            Back to Business
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter bp-utility-page bp-business-page bp-business-quote-page">
      <SEOHead
        title="Get a Quote"
        description="Tell us what you need and we'll send pricing for your bulk merch order within 24 hours."
        path="/business/quote"
        keywords={["bulk merch quote", "custom apparel quote", "company swag quote", "event merch pricing"]}
      />

      {/* Header */}
      <section className="bg-qds-background border-b border-qds-border" aria-labelledby="quote-heading">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-qds-text-tertiary mb-4">
            <Link to="/business" className="hover:text-qds-text-secondary transition-colors min-h-[44px] inline-flex items-center">
              Business
            </Link>
            <span aria-hidden="true">/</span>
            <span className="text-qds-text-primary" aria-current="page">Get a Quote</span>
          </nav>
          <h1 id="quote-heading" className="text-3xl md:text-4xl font-light tracking-tight text-qds-text-primary mb-2">
            Get a Quote
          </h1>
          <p className="text-qds-text-secondary text-sm">
            Tell us what you need. We'll have pricing to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate className="max-w-3xl mx-auto px-5 md:px-8 py-10">
        {/* Company Info */}
        <fieldset className="mb-10">
          <legend className="text-lg font-medium text-qds-text-primary mb-5">
            About You
          </legend>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <QuoteField
              id="quote-companyName"
              label="Company"
              value={companyName}
              onChange={(v) => { setCompanyName(v); clearFieldError("companyName"); }}
              error={fieldErrors["companyName"]}
              required
              placeholder="Acme Co."
              autoComplete="organization"
            />
            <QuoteField
              id="quote-contactName"
              label="Your Name"
              value={contactName}
              onChange={(v) => { setContactName(v); clearFieldError("contactName"); }}
              error={fieldErrors["contactName"]}
              required
              placeholder="Jane Smith"
              autoComplete="name"
            />
            <QuoteField
              id="quote-email"
              label="Email"
              type="email"
              value={email}
              onChange={(v) => { setEmail(v); clearFieldError("email"); }}
              error={fieldErrors["email"]}
              required
              placeholder="you@company.com"
              autoComplete="email"
            />
            <QuoteField
              id="quote-phone"
              label="Phone"
              type="tel"
              value={phone}
              onChange={(v) => setPhone(v)}
              placeholder="(555) 123-4567"
              autoComplete="tel"
            />
          </div>
        </fieldset>

        {/* Products */}
        <fieldset className="mb-10">
          <div className="flex items-center justify-between mb-5">
            <legend className="text-lg font-medium text-qds-text-primary">
              What Do You Need?
            </legend>
            <button
              type="button"
              onClick={addProduct}
              className="text-sm font-medium text-qds-text-secondary hover:text-qds-text-primary flex items-center gap-1 transition-colors min-h-[44px]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add another
            </button>
          </div>

          <div className="space-y-4">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="bg-qds-surface border border-qds-border rounded-xl p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-qds-text-primary">
                    Product {index + 1}
                  </span>
                  {products.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProduct(product.id)}
                      className="text-qds-text-tertiary hover:text-qds-error transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={`Remove product ${index + 1}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor={`product-type-${product.id}`} className="block text-xs font-medium text-qds-text-secondary mb-1">
                      Product Type <span aria-hidden="true">*</span>
                      <span className="sr-only">(required)</span>
                    </label>
                    <select
                      id={`product-type-${product.id}`}
                      value={product.product_type}
                      onChange={(e) => updateProduct(product.id, "product_type", e.target.value)}
                      required
                      aria-invalid={fieldErrors[`product_${index}_type`] ? true : undefined}
                      aria-describedby={fieldErrors[`product_${index}_type`] ? `product-type-error-${product.id}` : undefined}
                      className={cn(
                        "w-full px-3 py-2 bg-qds-surface-elevated border rounded-lg text-sm focus:outline-none transition-colors min-h-[44px]",
                        fieldErrors[`product_${index}_type`]
                          ? "border-qds-error"
                          : "border-qds-border focus:border-qds-border-strong"
                      )}
                    >
                      <option value="">Select product</option>
                      <option value="tshirt">T-Shirt</option>
                      <option value="hoodie">Hoodie</option>
                      <option value="crewneck">Crewneck</option>
                      <option value="hat">Hat</option>
                      <option value="mug">Mug</option>
                      <option value="tote">Tote Bag</option>
                      <option value="sticker">Sticker</option>
                      <option value="poster">Poster</option>
                      <option value="other">Other</option>
                    </select>
                    {fieldErrors[`product_${index}_type`] && (
                      <p id={`product-type-error-${product.id}`} className="field-error" role="alert">
                        {fieldErrors[`product_${index}_type`]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`product-qty-${product.id}`} className="block text-xs font-medium text-qds-text-secondary mb-1">
                      Quantity <span aria-hidden="true">*</span>
                      <span className="sr-only">(required)</span>
                    </label>
                    <input
                      id={`product-qty-${product.id}`}
                      type="number"
                      min="1"
                      value={product.quantity}
                      onChange={(e) => updateProduct(product.id, "quantity", e.target.value)}
                      required
                      aria-invalid={fieldErrors[`product_${index}_quantity`] ? true : undefined}
                      aria-describedby={fieldErrors[`product_${index}_quantity`] ? `product-qty-error-${product.id}` : undefined}
                      className={cn(
                        "w-full px-3 py-2 bg-qds-surface-elevated border rounded-lg text-sm focus:outline-none transition-colors min-h-[44px]",
                        fieldErrors[`product_${index}_quantity`]
                          ? "border-qds-error"
                          : "border-qds-border focus:border-qds-border-strong"
                      )}
                      placeholder="e.g., 100"
                    />
                    {fieldErrors[`product_${index}_quantity`] && (
                      <p id={`product-qty-error-${product.id}`} className="field-error" role="alert">
                        {fieldErrors[`product_${index}_quantity`]}
                      </p>
                    )}
                  </div>
                  <div>
                    <label htmlFor={`product-sizes-${product.id}`} className="block text-xs font-medium text-qds-text-secondary mb-1">
                      Sizes
                    </label>
                    <input
                      id={`product-sizes-${product.id}`}
                      type="text"
                      value={product.sizes}
                      onChange={(e) => updateProduct(product.id, "sizes", e.target.value)}
                      className="w-full px-3 py-2 bg-qds-surface-elevated border border-qds-border rounded-lg text-sm focus:outline-none focus:border-qds-border-strong transition-colors min-h-[44px]"
                      placeholder="e.g., S, M, L, XL"
                    />
                  </div>
                  <div>
                    <label htmlFor={`product-colors-${product.id}`} className="block text-xs font-medium text-qds-text-secondary mb-1">
                      Colors
                    </label>
                    <input
                      id={`product-colors-${product.id}`}
                      type="text"
                      value={product.colors}
                      onChange={(e) => updateProduct(product.id, "colors", e.target.value)}
                      className="w-full px-3 py-2 bg-qds-surface-elevated border border-qds-border rounded-lg text-sm focus:outline-none focus:border-qds-border-strong transition-colors min-h-[44px]"
                      placeholder="e.g., Black, White, Navy"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        {/* Additional Notes */}
        <div className="mb-10">
          <label htmlFor="quote-notes" className="block text-sm font-medium text-qds-text-primary mb-1.5">
            Anything Else?
          </label>
          <textarea
            id="quote-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            maxLength={2000}
            className="w-full px-4 py-3 bg-qds-surface border border-qds-border rounded-xl text-sm focus:outline-none focus:border-qds-border-strong resize-none transition-colors"
            placeholder="Timeline, design files, special requests — whatever we should know..."
          />
          <p className="text-xs text-qds-text-tertiary mt-1 text-right">{notes.length}/2000</p>
        </div>

        {submitError && (
          <div className="p-3 bg-qds-error-tint border border-qds-error/20 rounded-lg mb-6" role="alert">
            <p className="text-sm text-qds-error">{submitError}</p>
          </div>
        )}

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full sm:w-auto bg-qds-primary text-qds-text-inverse px-8 py-3.5 rounded-xl font-medium text-base hover:bg-qds-primary-pressed transition-colors disabled:opacity-60 min-h-[48px]"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" />
              Submitting...
            </span>
          ) : (
            "Send quote request"
          )}
        </button>
      </form>
    </div>
  );
}

// --- Reusable form field for quote form ---

function QuoteField({
  id,
  label,
  type = "text",
  value,
  onChange,
  error,
  required,
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-qds-text-primary mb-1.5">
        {label}
        {required && <span aria-hidden="true"> *</span>}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        className={cn(
          "w-full px-4 py-2.5 bg-qds-surface border rounded-lg text-sm focus:outline-none transition-colors min-h-[44px]",
          error
            ? "border-qds-error focus:border-qds-error"
            : "border-qds-border focus:border-qds-border-strong"
        )}
      />
      {error && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
